import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { cn } from '../cn.js';
import { STATUS_GLYPH } from '../generated/status.js';
import { StatusGlyph } from './icons.js';
/**
 * Card, notice, toast, empty state and eyebrow (§06).
 *
 * All four feedback surfaces are the same three parts — a glyph, a sentence,
 * and the action that answers it — differing only in where they appear and how
 * loudly they announce themselves. Building them from one place is what stops
 * "Undo" being a link on the toast and a button in the notice.
 */
export function Card({ children, 
/**
 * Off for a card that paints to its own edge — a measure's gradient hairline,
 * a table that runs to the border. The card keeps `overflow-hidden` either
 * way, so a full-bleed child is clipped to the radius rather than squaring
 * off the corner it sits in.
 */
padded = true, className, }) {
    // A real border and a real edge. A card that is only a slightly different
    // tint from the page behind it is a rectangle you have to look for.
    return (_jsx("div", { className: cn('bg-surface border-line shadow-e1 overflow-hidden rounded-lg border', padded && 'p-5', className), children: children }));
}
/**
 * A single number, with what it measures and how it is doing.
 *
 * The reference's dashboard row: a gradient hairline in the colour of the
 * thing, an eyebrow, the number at 40px, a line of context, and the tone's
 * glyph in a tinted tile.
 *
 * ## The tone means something
 *
 * It is not four colours to stop a row of white boxes looking plain. `brand`
 * is the shape of the day — visits, people, things that are neither good nor
 * bad. `warning` is a queue. `critical` is a queue that has started costing
 * money. `inactive` is a measure with nothing behind it. Colouring a measure
 * decoratively is how a screen ends up with a red number that means nothing and
 * a reader who has learned to ignore red.
 *
 * Colour is never the only signal: the glyph is the tone's own shape from the
 * status registry, and the label and sub-line say it in words. `pending`
 * renders an em dash rather than a `0`, because `0` is a claim that we counted.
 */
const MEASURE_TONE = {
    brand: { bar: 'bg-grad-brand', tile: 'bg-brand-soft text-brand', value: 'text-ink' },
    verified: {
        bar: 'bg-grad-verified',
        tile: 'bg-verified-bg text-verified',
        value: 'text-verified',
    },
    scheduled: {
        bar: 'bg-grad-scheduled',
        tile: 'bg-scheduled-bg text-scheduled',
        value: 'text-ink',
    },
    progress: {
        bar: 'bg-grad-progress',
        tile: 'bg-progress-bg text-progress',
        value: 'text-progress',
    },
    warning: { bar: 'bg-grad-warning', tile: 'bg-warning-bg text-warning', value: 'text-warning' },
    critical: {
        bar: 'bg-grad-critical',
        tile: 'bg-critical-bg text-critical',
        value: 'text-critical',
    },
    inactive: { bar: 'bg-grad-inactive', tile: 'bg-inactive-bg text-inactive', value: 'text-ink-3' },
};
export function Measure({ label, tone, 
/**
 * The measure's own mark — a calendar for visits, a clock for open shifts.
 *
 * The tone's status glyph is the fallback, and it is only right when the
 * measure genuinely *is* that status. Two measures that are both "the shape
 * of the day" would otherwise land on the same shape, which is the one thing
 * §05 says an icon must never do.
 */
icon, 
/** The number, when there is one. */
value, 
/** What has to exist before there is one. Never "No data". */
pending, note, className, }) {
    const style = MEASURE_TONE[tone];
    return (_jsxs(Card, { padded: false, className: className, children: [_jsx("div", { className: cn('h-1', style.bar) }), _jsxs("div", { className: "p-5", children: [_jsxs("div", { className: "flex items-start justify-between gap-3", children: [_jsx(Eyebrow, { children: label }), _jsx("span", { "aria-hidden": "true", className: cn('h-tile w-tile grid flex-none place-items-center rounded-md', style.tile), children: icon ?? (_jsx(StatusGlyph, { name: tone === 'brand' ? 'pulse' : STATUS_GLYPH[tone], className: "h-5 w-5" })) })] }), value === undefined ? (_jsxs(_Fragment, { children: [_jsx("p", { className: "text-ink-3 mt-2 text-xl font-normal leading-none", "aria-hidden": "true", children: "\u2014" }), _jsx("p", { className: "text-ink-3 mt-2 text-xs", children: pending })] })) : (_jsxs(_Fragment, { children: [_jsx("p", { className: cn('text-metric mt-2 font-bold leading-none', style.value), "data-numeric": true, children: value }), note && _jsx("p", { className: "text-ink-3 mt-2 text-xs", children: note })] }))] })] }));
}
/** §03's eyebrow: the one place uppercase is allowed. */
export function Eyebrow({ children, className, }) {
    return _jsx("p", { className: cn('eyebrow', className), children: children });
}
const TONE_SURFACE = {
    verified: 'text-verified bg-verified-bg border-verified-br',
    scheduled: 'text-scheduled bg-scheduled-bg border-scheduled-br',
    progress: 'text-progress bg-progress-bg border-progress-br',
    warning: 'text-warning bg-warning-bg border-warning-br',
    critical: 'text-critical bg-critical-bg border-critical-br',
    inactive: 'text-inactive bg-inactive-bg border-inactive-br',
};
/** The same tone on a raised surface, which keeps its own background. */
const TONE_EDGE = {
    verified: 'text-verified border-l-verified',
    scheduled: 'text-scheduled border-l-scheduled',
    progress: 'text-progress border-l-progress',
    warning: 'text-warning border-l-warning',
    critical: 'text-critical border-l-critical',
    inactive: 'text-inactive border-l-inactive',
};
/**
 * An inline message about the thing it sits next to.
 *
 * `critical` is announced assertively because it reports something that is
 * already blocking the reader; everything else is polite. Nothing here
 * animates, and nothing dismisses itself on a timer — a message that vanishes
 * before it is read is worse than no message.
 */
export function Notice({ tone, title, children, action, className, }) {
    return (_jsxs("div", { role: tone === 'critical' ? 'alert' : 'status', className: cn('rounded-md border px-4 py-3', TONE_SURFACE[tone], className), children: [_jsxs("p", { className: "flex items-center gap-2 text-sm font-semibold", children: [_jsx(StatusGlyph, { name: STATUS_GLYPH[tone], className: "h-4 w-4" }), title] }), children && _jsx("div", { className: "text-ink-2 mt-1 text-sm", children: children }), action && _jsx("div", { className: "mt-3", children: action })] }));
}
/**
 * A message about something that has just happened elsewhere on the screen.
 *
 * Same anatomy as `Notice`, raised onto the overlay layer. It carries the undo
 * rather than offering a dismiss: "Shift filled · Amara claimed Tuesday 9:00 AM
 * · Undo" tells the reader what changed and gives them the way back.
 */
export function Toast({ tone, title, children, action, className, }) {
    return (_jsxs("div", { role: tone === 'critical' ? 'alert' : 'status', className: cn('bg-surface border-line border-l-edge shadow-e3 rounded-md border p-4', TONE_EDGE[tone], className), children: [_jsxs("p", { className: "flex items-center gap-2 text-sm font-semibold", children: [_jsx(StatusGlyph, { name: STATUS_GLYPH[tone], className: "h-4 w-4" }), title] }), children && _jsx("div", { className: "text-ink-2 mt-1 text-sm", children: children }), action && _jsx("div", { className: "mt-2", children: action })] }));
}
/**
 * An empty screen is an invitation to act (§06, §12).
 *
 * It says what belongs here and offers the action that puts it there. "No data
 * available" is not an empty state; it is a shrug, and a blank panel reads as a
 * broken product.
 */
export function EmptyState({ title, explanation, action, className, }) {
    // Centred in the space it is standing in for, as the reference does it. An
    // empty state pinned to the top-left reads as content that failed to load;
    // the same words in the middle of the panel read as a panel that is ready and
    // waiting.
    return (_jsxs("div", { className: cn('grid place-items-center px-4 py-12 text-center', className), children: [_jsx("h2", { className: "text-ink text-md font-semibold", children: title }), _jsx("p", { className: "text-ink-3 mt-1 max-w-prose text-sm", children: explanation }), action && _jsx("div", { className: "mt-5", children: action })] }));
}
/**
 * The top of a screen: what it is, how it is doing, and the one thing to do.
 *
 * Every screen in the reference opens the same way — a 34px title, a grey line
 * of counts under it, and at most one action on the right. Having it here
 * rather than in each page is what stops the third screen someone builds using
 * a 30px title and putting its action underneath.
 *
 * `meta` is the counts line and takes the domain's own separator: "24 on the
 * roster · 20 active". It is a sentence about the data, never a description of
 * the page.
 */
export function PageHeader({ title, meta, action, className, }) {
    return (_jsxs("div", { className: cn('flex flex-wrap items-start justify-between gap-4', className), children: [_jsxs("div", { className: "min-w-0", children: [_jsx("h1", { className: "text-ink text-display font-bold", children: title }), meta && _jsx("p", { className: "text-ink-3 text-md mt-1", children: meta })] }), action && _jsx("div", { className: "flex flex-none items-center gap-2", children: action })] }));
}
//# sourceMappingURL=surfaces.js.map